import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coversApi, catalogApi, type Cover } from '@/lib/api';
import { CatalogBar } from './covers/CatalogBar';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { CoverThumbnail } from '@/components/CoverThumbnail';
import { UploadCoverDialog } from './covers/UploadCoverDialog';
import { CoverEditDialog } from './covers/CoverEditDialog';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

type SortOption = 'popular' | 'alpha_az' | 'alpha_za' | 'newest' | 'oldest' | 'updated';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'popular', label: 'Populaires' },
  { value: 'alpha_az', label: 'A → Z' },
  { value: 'alpha_za', label: 'Z → A' },
  { value: 'newest', label: 'Plus récents' },
  { value: 'oldest', label: 'Plus anciens' },
  { value: 'updated', label: 'Dernière modification' },
];

function normalizeSearch(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export default function AdminCoversPage() {
  const { t } = useT();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editingCover, setEditingCover] = useState<Cover | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('popular');

  useEffect(() => {
    document.title = t('Couvertures — Admin Storiz');
  }, [t]);

  const { data: categoriesData } = useQuery({
    queryKey: ['cover-categories'],
    queryFn: () => coversApi.categories(),
  });

  const { data: coversData, isLoading } = useQuery({
    queryKey: ['admin-covers', selectedCategory],
    queryFn: () =>
      coversApi.list({
        include_hidden: true,
        ...(selectedCategory ? { category: selectedCategory } : {}),
      }),
  });

  const categories = categoriesData?.categories ?? [];
  const rawCovers = coversData?.covers ?? [];
  // Views and « Je choisis » per cover in the public catalog (migration 252; empty before it).
  const { data: catalogStats } = useQuery({ queryKey: ['catalog-stats'], queryFn: () => catalogApi.stats(), staleTime: 60_000 });

  // Filter by search
  const filteredCovers = useMemo(() => {
    if (!searchQuery.trim()) return rawCovers;
    const q = normalizeSearch(searchQuery);
    return rawCovers.filter((cover) => {
      const haystack = normalizeSearch(
        [
          cover.name,
          ...(cover.categories ?? []),
          cover.description,
          cover.id,
          ...(cover.search_tags ?? []),
        ].join(' ')
      );
      return haystack.includes(q);
    });
  }, [rawCovers, searchQuery]);

  // Sort
  const sortedCovers = useMemo(() => {
    const arr = [...filteredCovers];
    switch (sortBy) {
      case 'alpha_az':
        return arr.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
      case 'alpha_za':
        return arr.sort((a, b) => b.name.localeCompare(a.name, 'fr'));
      case 'newest':
        return arr.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case 'oldest':
        return arr.sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      case 'updated':
        return arr.sort(
          (a, b) =>
            new Date(b.updated_at ?? b.created_at).getTime() -
            new Date(a.updated_at ?? a.created_at).getTime()
        );
      case 'popular':
      default:
        return arr.sort((a, b) => (b.usage_count ?? 0) - (a.usage_count ?? 0));
    }
  }, [filteredCovers, sortBy]);

  return (
    <AdminLayout
      title={t('Bibliothèque de couvertures')}
      subtitle={
        sortedCovers.length !== 1
          ? (searchQuery.trim()
              ? t('{n} couvertures pour « {q} »', { n: sortedCovers.length, q: searchQuery })
              : t('{n} couvertures', { n: sortedCovers.length }))
          : (searchQuery.trim()
              ? t('{n} couverture pour « {q} »', { n: sortedCovers.length, q: searchQuery })
              : t('{n} couverture', { n: sortedCovers.length }))
      }
      actions={
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* The public catalog (owner, 2026-10-17): switch, « Lien client », one link per category. */}
          <CatalogBar categories={categories} current={selectedCategory} />
          <Button onClick={() => setUploadOpen(true)}>
            {t('+ Ajouter une couverture')}
          </Button>
        </div>
      }
    >
      {/* Search bar + Sort dropdown */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Rechercher un design (nom, slug, tags)...')}
            className="w-full pl-10 pr-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer min-w-[160px]"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.label)}
            </option>
          ))}
        </select>
      </div>

      {/* Category tabs */}
      <div
        className={cn(
          'bg-white rounded-xl border border-gray-200 p-2 mb-5 flex gap-1 overflow-x-auto',
          searchQuery.trim() ? 'opacity-60' : ''
        )}
      >
        <button
          onClick={() => setSelectedCategory('')}
          className={cn(
            'px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
            !selectedCategory
              ? 'bg-brand-500 text-white'
              : 'text-gray-600 hover:bg-gray-50'
          )}
        >
          {t('Toutes')}
        </button>
        {categories.map((cat) => (
          <button
            key={cat.slug}
            onClick={() => setSelectedCategory(cat.slug)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
              selectedCategory === cat.slug
                ? 'bg-brand-500 text-white'
                : 'text-gray-600 hover:bg-gray-50'
            )}
          >
            {cat.icon && <span className="mr-1">{cat.icon}</span>}
            {cat.label_fr}
          </button>
        ))}
      </div>

      {/* Covers grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="aspect-[3/4] bg-white rounded-xl border border-gray-100 animate-pulse"
            />
          ))}
        </div>
      ) : sortedCovers.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <div className="text-5xl mb-3">{searchQuery.trim() ? '🔍' : '🖼️'}</div>
          <h3 className="font-semibold text-gray-900 mb-1">
            {searchQuery.trim()
              ? t('Aucun design trouvé pour « {q} »', { q: searchQuery })
              : t('Aucune couverture')}
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            {searchQuery.trim()
              ? t('Essayez un autre mot-clé ou effacez la recherche')
              : t('Ajoutez votre première couverture pour commencer')}
          </p>
          {searchQuery.trim() ? (
            <button
              onClick={() => setSearchQuery('')}
              className="text-brand-500 text-sm hover:underline"
            >
              {t('Effacer la recherche')}
            </button>
          ) : (
            <Button onClick={() => setUploadOpen(true)}>
              {t('+ Ajouter une couverture')}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {sortedCovers.map((cover) => (
            <CoverCard
              key={cover.id}
              cover={cover}
              stats={catalogStats?.stats?.[cover.id] ?? null}
              onClick={() => setEditingCover(cover)}
            />
          ))}
        </div>
      )}

      {uploadOpen && (
        <UploadCoverDialog
          isOpen={uploadOpen}
          onClose={() => setUploadOpen(false)}
          categories={categories}
        />
      )}

      {editingCover && (
        <CoverEditDialog
          cover={editingCover}
          categories={categories}
          onClose={() => setEditingCover(null)}
        />
      )}
    </AdminLayout>
  );
}

function CoverCard({
  cover,
  stats,
  onClick,
}: {
  cover: Cover;
  stats: { views: number; chooses: number } | null;
  onClick: () => void;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const toggleVisibility = useMutation({
    mutationFn: () =>
      coversApi.update(cover.id, { is_visible: !cover.is_visible }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['admin-covers'] }),
  });
  // « Visible clients » — the public catalog only (migration 252; missing = visible).
  const clientVisible = cover.public_visible !== false;
  const toggleClient = useMutation({
    mutationFn: () => coversApi.update(cover.id, { public_visible: !clientVisible } as Partial<Cover>),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-covers'] }),
  });

  return (
    <div className="group relative bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
      <button onClick={onClick} className="block w-full">
        <CoverThumbnail cover={cover} className="rounded-t-xl" />
        {!cover.is_visible && (
          <div className="absolute top-2 right-2 bg-gray-800 text-white text-xs px-2 py-0.5 rounded-full z-10">
            {t('Masquée')}
          </div>
        )}
        <div className="p-3">
          <p className="font-medium text-gray-900 text-sm truncate text-left">
            {cover.name}
          </p>
          <p className="text-xs text-gray-500 capitalize text-left">
            {(cover.categories ?? []).join(' · ')}
          </p>
        </div>
      </button>
      <div className="px-3 pb-3 -mt-1 flex items-center justify-between gap-2">
        <label className="flex items-center gap-1.5 text-[11.5px] text-gray-600 cursor-pointer select-none" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={clientVisible} disabled={toggleClient.isPending} onChange={() => toggleClient.mutate()} className="w-3.5 h-3.5" />
          {t('Visible clients')}
        </label>
        {stats && (stats.views > 0 || stats.chooses > 0) && (
          <span className="text-[11px] text-gray-500 tabular-nums" title={t('Vues et « Je choisis » dans le catalogue public')}>
            👁 {stats.views} · ✓ {stats.chooses}
          </span>
        )}
      </div>

      {/* Quick actions overlay */}
      <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleVisibility.mutate();
          }}
          className="w-8 h-8 bg-white/90 hover:bg-white rounded-lg flex items-center justify-center text-sm shadow"
          title={cover.is_visible ? t('Masquer') : t('Afficher')}
        >
          {cover.is_visible ? '👁️' : '🚫'}
        </button>
      </div>
    </div>
  );
}
